import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../../../../../api/apiClient";

const STATUSES = [
    { value: "new",       label: "New",       color: "#3b82f6" },
    { value: "active",    label: "Active",    color: "#10b981" },
    { value: "won",       label: "Won",       color: "#22c55e" },
    { value: "lost",      label: "Lost",      color: "#ef4444" },
    { value: "converted", label: "Converted", color: "#8b5cf6" },
];

const PRIORITIES = [
    { value: "hot",  label: "Hot",  color: "#ef4444" },
    { value: "warm", label: "Warm", color: "#f59e0b" },
    { value: "cold", label: "Cold", color: "#3b82f6" },
];

const SOURCES = [
    { value: "website",       label: "Website",       color: "#6366f1" },
    { value: "referral",      label: "Referral",      color: "#10b981" },
    { value: "linkedin",      label: "LinkedIn",      color: "#0ea5e9" },
    { value: "trade_show",    label: "Trade Show",    color: "#f59e0b" },
    { value: "cold_outreach", label: "Cold Outreach", color: "#ef4444" },
    { value: "partner",       label: "Partner",       color: "#8b5cf6" },
    { value: "other",         label: "Other",         color: "#94a3b8" },
];

// ── Reusable collapsible filter section ──────────────────────────────────────

function FilterSection({ accentColor, label, selected, children }) {
    const [open, setOpen] = useState(false);
    const count = selected.length;
    
    return (
        <div className="ab-section">
            <button
                type="button"
                className={`ab-section-header${open ? " ab-section-header--open" : ""}`}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="ab-section-dot" style={{ background: accentColor }} />
                <span className="ab-section-label">{label}</span>
                <span className="ab-section-summary">
                    {count === 0
                        ? <span className="ab-section-any">Any</span>
                        : <span className="ab-section-count" style={{ background: accentColor }}>{count}</span>
                    }
                </span>
                <svg
                    className={`ab-section-chevron${open ? " ab-section-chevron--open" : ""}`}
                    width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <div className="ab-section-body">
                    {children}
                </div>
            )}
        </div>
    );
}

// ── Multi-select pill group ───────────────────────────────────────────────────

function PillGroup({ options, selected, onToggle, getColor }) {
    return (
        <div className="ab-pills">
            {options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                const color = getColor ? getColor(opt) : opt.color;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        className={`ab-pill${isSelected ? " ab-pill--active" : ""}`}
                        style={isSelected
                            ? { background: color, borderColor: color, color: "#fff" }
                            : { borderColor: color + "55", color: color }
                        }
                        onClick={() => onToggle(opt.value)}
                    >
                        {isSelected && (
                            <span className="ab-pill-dot" style={{ background: "#ffffff88" }} />
                        )}
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

const AudienceBuilder = ({ filters, audienceType, onFiltersChange, onAudienceTypeChange, stages, admins, safetyRules }) => {
    const [count, setCount]             = useState(null);
    const [preview, setPreview]         = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [loading, setLoading]         = useState(false);
    const debounceRef = useRef(null);

    // Normalise filter arrays (handle legacy single-string format)
    const sel = {
        statuses:   Array.isArray(filters.statuses)   ? filters.statuses   : filters.status   ? [filters.status]   : [],
        priorities: Array.isArray(filters.priorities) ? filters.priorities : filters.priority ? [filters.priority] : [],
        sources:    Array.isArray(filters.sources)    ? filters.sources    : filters.source   ? [filters.source]   : [],
        stageIds:   Array.isArray(filters.stageIds)   ? filters.stageIds   : filters.stageId  ? [filters.stageId]  : [],
        assignedTo: filters.assignedTo || "",
    };

    const toggle = (field, value) => {
        const current = sel[field];
        const next = current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
        onFiltersChange({ ...filters, [field]: next });
    };

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await apiClient.post("/admin/crm/campaigns/resolve-audience", {
                    filters: sel,
                    allowedStageIds: stages.map((s) => s.id),
                    excludeLost: safetyRules?.excludeLost,
                    skipContactedDays: safetyRules?.skipContactedDays || null,
                });
                setCount(res.data.count);
                setPreview(res.data.preview || []);
            } catch {
                setCount(null);
            } finally {
                setLoading(false);
            }
        }, 500);
        return () => clearTimeout(debounceRef.current);
    }, [filters, stages, safetyRules?.excludeLost, safetyRules?.skipContactedDays]);

    const stageOptions = stages.map((s) => ({ value: s.id, label: s.name, color: s.color }));

    return (
        <div className="ab-root">

            {/* Audience type toggle */}
            <div className="crm-option-pills" style={{ marginBottom: "var(--space-4)" }}>
                <button
                    type="button"
                    className={`crm-option-pill${audienceType === "dynamic" ? " crm-option-pill--active" : ""}`}
                    onClick={() => onAudienceTypeChange("dynamic")}
                >
                    Dynamic
                </button>
                <button
                    type="button"
                    className={`crm-option-pill${audienceType === "snapshot" ? " crm-option-pill--active" : ""}`}
                    onClick={() => onAudienceTypeChange("snapshot")}
                >
                    Snapshot
                </button>
            </div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", marginBottom: "var(--space-5)", lineHeight: 1.4 }}>
                {audienceType === "dynamic"
                    ? "Audience re-evaluates each time the campaign runs."
                    : "Audience is locked to leads at launch time."}
            </div>

            {/* Filter sections */}
            <div className="ab-sections">

                <FilterSection accentColor="#3b82f6" label="STATUS" selected={sel.statuses}>
                    <PillGroup
                        options={STATUSES}
                        selected={sel.statuses}
                        onToggle={(v) => toggle("statuses", v)}
                    />
                </FilterSection>

                <FilterSection accentColor="#f59e0b" label="PRIORITY" selected={sel.priorities}>
                    <PillGroup
                        options={PRIORITIES}
                        selected={sel.priorities}
                        onToggle={(v) => toggle("priorities", v)}
                    />
                </FilterSection>

                <FilterSection accentColor="#8b5cf6" label="STAGE" selected={sel.stageIds}>
                    <PillGroup
                        options={stageOptions}
                        selected={sel.stageIds.map(Number)}
                        onToggle={(v) => {
                            const num = Number(v);
                            const current = sel.stageIds.map(Number);
                            const next = current.includes(num)
                                ? current.filter((id) => id !== num)
                                : [...current, num];
                            onFiltersChange({ ...filters, stageIds: next });
                        }}
                    />
                </FilterSection>

                <FilterSection accentColor="#6366f1" label="SOURCE" selected={sel.sources}>
                    <PillGroup
                        options={SOURCES}
                        selected={sel.sources}
                        onToggle={(v) => toggle("sources", v)}
                    />
                </FilterSection>

                {/* Rep — single select, kept as dropdown */}
                <div className="ab-rep-row">
                    <span className="ab-rep-label">REP</span>
                    <select
                        className="ab-rep-select"
                        value={sel.assignedTo}
                        onChange={(e) => onFiltersChange({ ...filters, assignedTo: e.target.value })}
                    >
                        <option value="">Any</option>
                        {admins.map((a) => (
                            <option key={a.userId} value={a.userId}>
                                {a.firstName} {a.lastName}
                            </option>
                        ))}
                    </select>
                </div>

            </div>

            {/* Live count */}
            <div className="ab-count-panel">
                <div className="crm-audience-hero">
                    <span className="crm-audience-hero__number">
                        {loading ? "…" : count !== null ? count : "—"}
                    </span>
                    <span className="crm-audience-hero__label">
                        {count !== null && !loading ? `lead${count !== 1 ? "s" : ""} match` : loading ? "calculating…" : ""}
                    </span>
                </div>
                {preview.length > 0 && (
                    <>
                        <button
                            type="button"
                            className="crm-audience-preview-btn"
                            onClick={() => setShowPreview((v) => !v)}
                        >
                            {showPreview ? "Hide preview" : `Preview ${count > 10 ? "10 of " + count : count} lead${count !== 1 ? "s" : ""} →`}
                        </button>
                        {showPreview && (
                            <div style={{ marginTop: "var(--space-2)", display: "flex", flexDirection: "column", gap: 2 }}>
                                {preview.map((l) => (
                                    <div key={l.id} style={{ fontSize: "11px", color: "var(--color-text-secondary)" }}>
                                        {l.firstName} {l.lastName} &lt;{l.email}&gt;
                                    </div>
                                ))}
                                {count > preview.length && (
                                    <div style={{ fontSize: "11px", color: "var(--color-text-secondary)", fontStyle: "italic" }}>
                                        …and {count - preview.length} more
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AudienceBuilder;
